import { useEffect, useState } from 'react'
import { fetchSingleConsumer } from '@/services/consumerService'
import { treesOfConsumer, type MeterTrees } from '@/utils/premiseTree'

const ALL: Record<'no-consumer' | 'loading' | 'failed', MeterTrees> = {
  'no-consumer': {
    parent: { status: 'no-consumer' },
    supplier: { status: 'no-consumer' },
  },
  loading: { parent: { status: 'loading' }, supplier: { status: 'loading' } },
  failed: { parent: { status: 'failed' }, supplier: { status: 'failed' } },
}

type Answer = {
  siteId: string
  meterNumber: string | number
  trees: MeterTrees
}

/**
 * The trees of a meter picked from a list that does not carry its parentId or supplierOfId.
 * The single-consumer call runs only while `enabled`, and once per meter: a failure is asked
 * again the next time the caller enables it.
 */
export const useMeterTreeState = (
  siteId: string | undefined,
  meterNumber: string | number | null | undefined,
  enabled: boolean
): MeterTrees => {
  const [answer, setAnswer] = useState<Answer | null>(null)
  const hasMeter = Boolean(siteId) && meterNumber != null && meterNumber !== ''
  const answered =
    answer != null &&
    answer.siteId === siteId &&
    answer.meterNumber === meterNumber
      ? answer.trees
      : null
  const needsCall =
    enabled &&
    hasMeter &&
    (answered == null || answered.parent.status === 'failed')

  useEffect(() => {
    if (!needsCall || !siteId || meterNumber == null) return
    let cancelled = false
    fetchSingleConsumer(siteId, meterNumber).then(result => {
      if (cancelled) return
      setAnswer({
        siteId,
        meterNumber,
        trees: result.ok ? treesOfConsumer(result.data) : ALL.failed,
      })
    })
    return () => {
      cancelled = true
    }
  }, [needsCall, siteId, meterNumber])

  if (!hasMeter) return ALL['no-consumer']
  if (answered) return answered
  return enabled ? ALL.loading : ALL['no-consumer']
}
